import React, { useState, useCallback } from "react";
import {
  Package, Sparkles, Camera, FileText, ChevronRight, ChevronLeft,
  Check, Star, Image, Wand2, Loader2, Upload, ArrowRight
} from "lucide-react";
import { API_URL } from "../../constants";

// ─── Package mapping from DNA results ───
const DNA_PACKAGE_MAP = {
  "under 4000": "Basic",
  "4000-6000": "Classic",
  "6000-10000": "Premium",
  "10000-15000": "Luxury",
  "above 15000": "Luxury",
};

const PACKAGES = [
  { id: "Basic", price: "3,999", color: "#10B981", desc: "Balloon arch, banner, table setup" },
  { id: "Classic", price: "5,999", color: "#3B82F6", desc: "Premium backdrop, LED lights, balloon garland" },
  { id: "Premium", price: "8,999", color: "#7C3AED", desc: "Full room decor, neon sign, flower arrangements" },
  { id: "Luxury", price: "14,999", color: "#E83E8C", desc: "Grand setup, fog machine, photo booth, everything" },
];

const FLOW_STEPS = [
  { id: "package", label: "Your Package", icon: Package },
  { id: "addons", label: "Add-ons", icon: Sparkles },
  { id: "magic", label: "Preview", icon: Image },
  { id: "booking", label: "Book It", icon: FileText },
];

// ═══════════════════════════════════════════════════════════════
// Step 1: Package Selection (auto-highlighted from DNA)
// ═══════════════════════════════════════════════════════════════
const PackageStep = ({ recommended, selected, onSelect }) => (
  <div className="space-y-4" data-testid="experience-step-package">
    <div className="text-center mb-2">
      <h3 className="text-xl font-bold text-[#1E1B4B]">Choose Your Package</h3>
      <p className="text-xs text-[#94a3b8] mt-1">Based on your Party DNA, we recommend <strong className="text-[#E83E8C]">{recommended}</strong></p>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {PACKAGES.map(pkg => {
        const isRecommended = pkg.id === recommended;
        const isSelected = pkg.id === selected;
        return (
          <button key={pkg.id} onClick={() => onSelect(pkg.id)}
            className={`relative p-4 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] ${
              isSelected ? "border-[#E83E8C] bg-[#E83E8C]/5 shadow-lg" : isRecommended ? "border-amber-400 bg-amber-50/50" : "border-gray-200 bg-white hover:border-gray-300"
            }`} data-testid={`pkg-${pkg.id}`}>
            {isRecommended && !isSelected && (
              <span className="absolute -top-2 left-3 px-2 py-0.5 bg-amber-400 text-white text-[9px] font-bold rounded-full">RECOMMENDED</span>
            )}
            {isSelected && (
              <span className="absolute -top-2 right-3 w-5 h-5 bg-[#E83E8C] rounded-full flex items-center justify-center"><Check size={12} className="text-white" /></span>
            )}
            <p className="text-sm font-bold" style={{ color: pkg.color }}>{pkg.id}</p>
            <p className="text-xl font-bold text-[#1E1B4B] mt-1">Rs {pkg.price}</p>
            <p className="text-[10px] text-[#94a3b8] mt-1">{pkg.desc}</p>
          </button>
        );
      })}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Step 2: Add-ons (from AI Upsell) — simplified inline version
// ═══════════════════════════════════════════════════════════════
const AddonsStep = ({ upsells, selectedAddons, onToggle }) => (
  <div className="space-y-4" data-testid="experience-step-addons">
    <div className="text-center mb-2">
      <h3 className="text-xl font-bold text-[#1E1B4B]">Personalize Your Party</h3>
      <p className="text-xs text-[#94a3b8] mt-1">People with your personality often add these</p>
    </div>
    {upsells.length > 0 ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {upsells.map(item => {
          const isAdded = selectedAddons.includes(item.id);
          return (
            <button key={item.id} onClick={() => onToggle(item.id)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                isAdded ? "border-[#25D366] bg-[#25D366]/5" : "border-gray-200 hover:border-gray-300"
              }`} data-testid={`addon-${item.id}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isAdded ? "bg-[#25D366]" : "bg-gray-100"}`}>
                {isAdded ? <Check size={14} className="text-white" /> : <Sparkles size={14} className="text-[#94a3b8]" />}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-[#1E1B4B]">{item.name}</p>
                <p className="text-[10px] text-[#94a3b8]">{item.reason}</p>
              </div>
              <span className="text-xs font-bold text-[#E83E8C]">+Rs {item.price?.toLocaleString()}</span>
            </button>
          );
        })}
      </div>
    ) : (
      <p className="text-center text-xs text-[#94a3b8] py-6">Loading personalized suggestions...</p>
    )}
    <button onClick={() => onToggle("skip")}
      className="text-xs text-[#94a3b8] hover:text-[#475569] mx-auto block" data-testid="skip-addons">
      Skip add-ons for now
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Step 3: Magic Image Preview
// ═══════════════════════════════════════════════════════════════
const MagicStep = ({ selectedPackage, occasion, onImageGenerated, onSkip }) => {
  const [roomPhoto, setRoomPhoto] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRoomPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const formData = new FormData();
      if (roomPhoto) {
        const blob = await fetch(roomPhoto).then(r => r.blob());
        formData.append("image", blob, "room.jpg");
      }
      formData.append("package", selectedPackage);
      formData.append("occasion", occasion || "Birthday");

      const res = await fetch(`${API_URL}/api/magic/generate`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewImage(data.images?.[0] || null);
        if (onImageGenerated) onImageGenerated(data.images?.[0]);
      }
    } catch {
      // Silent fail — preview is optional
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-4" data-testid="experience-step-magic">
      <div className="text-center mb-2">
        <h3 className="text-xl font-bold text-[#1E1B4B]">See Your Party Come Alive</h3>
        <p className="text-xs text-[#94a3b8] mt-1">Upload your room photo and see how your {selectedPackage} setup will look!</p>
      </div>

      {!previewImage ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center">
          {roomPhoto ? (
            <div className="space-y-4">
              <img src={roomPhoto} alt="Your room" className="max-h-48 mx-auto rounded-xl" />
              <button onClick={handleGenerate} disabled={generating}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-50 inline-flex items-center gap-2" data-testid="generate-preview-btn">
                {generating ? <><Loader2 size={16} className="animate-spin" /> Creating Magic...</> : <><Wand2 size={16} /> Generate Preview</>}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <Camera size={40} className="text-[#E83E8C]/30 mx-auto" />
              <p className="text-sm text-[#475569]">Upload a photo of your room/venue</p>
              <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E83E8C] text-white text-sm font-medium cursor-pointer hover:bg-[#d1356f] transition-colors" data-testid="upload-room-btn">
                <Upload size={14} /> Upload Photo
                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
              </label>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center space-y-3">
          <img src={`data:image/jpeg;base64,${previewImage}`} alt="Magic Preview" className="max-w-md mx-auto rounded-2xl shadow-lg" data-testid="magic-preview-image" />
          <p className="text-xs text-emerald-600 font-medium">Your {selectedPackage} setup preview is ready!</p>
        </div>
      )}

      <button onClick={onSkip}
        className="text-xs text-[#94a3b8] hover:text-[#475569] mx-auto block mt-2" data-testid="skip-magic">
        Skip preview, go to booking
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Step 4: Booking Form (pre-filled)
// ═══════════════════════════════════════════════════════════════
const BookingStep = ({ selectedPackage, occasion, personality, selectedAddons, upsells }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [address, setAddress] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const addonsTotal = selectedAddons.reduce((sum, id) => {
    const item = upsells.find(u => u.id === id);
    return sum + (item?.price || 0);
  }, 0);

  const pkgPrice = PACKAGES.find(p => p.id === selectedPackage)?.price?.replace(",", "") || "0";
  const total = parseInt(pkgPrice) + addonsTotal;

  const handleBook = async () => {
    if (!name || !phone || !date) return;
    setSubmitting(true);
    try {
      // Scroll to the main booking section with pre-filled data
      const params = new URLSearchParams({
        package: selectedPackage,
        occasion: occasion || "",
        name, phone, date,
        source: "party_dna",
      });
      window.location.href = `/#book-online?${params.toString()}`;
    } catch {
      // Fallback
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="booking-submitted">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-emerald-500" />
        </div>
        <h3 className="text-xl font-bold text-[#1E1B4B]">Redirecting to booking...</h3>
        <p className="text-sm text-[#94a3b8] mt-1">Your party details are ready!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="experience-step-booking">
      <div className="text-center mb-2">
        <h3 className="text-xl font-bold text-[#1E1B4B]">Book Your {personality} Party!</h3>
        <p className="text-xs text-[#94a3b8] mt-1">Almost there — just a few details</p>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-[#E83E8C]/5 to-[#7C3AED]/5 rounded-xl p-4 border border-[#E83E8C]/10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-[#1E1B4B]">{selectedPackage} Package</span>
          <span className="text-sm font-bold text-[#E83E8C]">Rs {parseInt(pkgPrice).toLocaleString()}</span>
        </div>
        {selectedAddons.length > 0 && (
          <div className="text-[10px] text-[#475569] space-y-0.5">
            {selectedAddons.map(id => {
              const item = upsells.find(u => u.id === id);
              return item ? <div key={id} className="flex justify-between"><span>+ {item.name}</span><span>Rs {item.price?.toLocaleString()}</span></div> : null;
            })}
          </div>
        )}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E83E8C]/10">
          <span className="text-sm font-bold text-[#1E1B4B]">Total</span>
          <span className="text-lg font-bold text-[#E83E8C]">Rs {total.toLocaleString()}</span>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Your Name"
          className="px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="booking-name" />
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone Number"
          className="px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="booking-phone" />
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="booking-date" />
        <input value={occasion || ""} readOnly placeholder="Occasion"
          className="px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50" />
      </div>
      <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Party Venue Address"
        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm" data-testid="booking-address" />

      <button onClick={handleBook} disabled={!name || !phone || !date || submitting}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold text-base hover:opacity-90 transition-all disabled:opacity-40 flex items-center justify-center gap-2 shadow-lg" data-testid="confirm-booking-btn">
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <><Sparkles size={18} /> Confirm & Book — Rs {total.toLocaleString()}</>}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN: Party Experience Flow
// ═══════════════════════════════════════════════════════════════
const PartyExperienceFlow = ({ personality, answers, onClose }) => {
  const [step, setStep] = useState(0);
  const recommended = DNA_PACKAGE_MAP[answers?.budget] || "Classic";
  const [selectedPackage, setSelectedPackage] = useState(recommended);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [upsells, setUpsells] = useState([]);

  // Fetch upsells
  React.useEffect(() => {
    if (!personality) return;
    fetch(`${API_URL}/api/party-dna/upsell`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        personality_name: personality.personality_name,
        budget: answers?.budget,
        occasion: answers?.occasion,
        vibe: answers?.vibe,
      }),
    })
      .then(r => r.json())
      .then(d => setUpsells(d.suggestions || []))
      .catch(() => {});
  }, [personality, answers]);

  const toggleAddon = (id) => {
    if (id === "skip") return setStep(2);
    setSelectedAddons(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const canNext = () => {
    if (step === 0) return !!selectedPackage;
    return true;
  };

  return (
    <div className="space-y-6" data-testid="party-experience-flow">
      {/* Progress bar */}
      <div className="flex items-center gap-1 mb-2">
        {FLOW_STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isDone = i < step;
          return (
            <React.Fragment key={s.id}>
              <button onClick={() => i <= step && setStep(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium transition-all ${
                  isActive ? "bg-[#E83E8C] text-white shadow-md" : isDone ? "bg-[#E83E8C]/10 text-[#E83E8C]" : "bg-gray-100 text-[#94a3b8]"
                }`} data-testid={`flow-step-${s.id}`}>
                {isDone ? <Check size={10} /> : <Icon size={10} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < FLOW_STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded-full ${isDone ? "bg-[#E83E8C]" : "bg-gray-200"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content */}
      {step === 0 && <PackageStep recommended={recommended} selected={selectedPackage} onSelect={setSelectedPackage} />}
      {step === 1 && <AddonsStep upsells={upsells} selectedAddons={selectedAddons} onToggle={toggleAddon} />}
      {step === 2 && <MagicStep selectedPackage={selectedPackage} occasion={answers?.occasion} onSkip={() => setStep(3)} />}
      {step === 3 && <BookingStep selectedPackage={selectedPackage} occasion={answers?.occasion} personality={personality?.personality_name} selectedAddons={selectedAddons} upsells={upsells} />}

      {/* Navigation */}
      {step < 3 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          {step > 0 ? (
            <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#1E1B4B]" data-testid="flow-prev">
              <ChevronLeft size={16} /> Back
            </button>
          ) : (
            <button onClick={onClose} className="text-xs text-[#94a3b8] hover:text-[#475569]">Back to results</button>
          )}
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext()}
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#7C3AED] text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-40 shadow-md" data-testid="flow-next">
            {step === 2 ? "Skip to Booking" : "Continue"} <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PartyExperienceFlow;
